// @flow
import { conversationIndicatorStatus } from '../../utils/texting';

export const NEEDS_HELP_STATUS = 'NEEDS_HELP';

export const DEFAULT_MESSAGE_STATUSES = [
  'NEW',
  'UNRESOLVED',
  NEEDS_HELP_STATUS,
];

/**
 * The Message Status buttons that should read as selected for the current URL.
 *
 * No `status` param at all means the queue was just opened, so the defaults apply. A bare
 * `status=` is what the filter writes when the user turns every button off, and it reads as an
 * empty selection, which the API treats as every status.
 */
export const selectedStatusesFromSearch = (
  search: string,
  isNeedsHelpFilterShown: boolean,
): string[] => {
  const searchParams = new URLSearchParams(search);
  const statuses = searchParams.has('status')
    ? searchParams.getAll('status').filter(Boolean)
    : DEFAULT_MESSAGE_STATUSES;

  return isNeedsHelpFilterShown
    ? statuses
    : statuses.filter((status) => status !== NEEDS_HELP_STATUS);
};

/**
 * The statuses to send to the API for the buttons the user has selected.
 *
 * The API filters on the stored status, but a card shows `conversationIndicatorStatus`. A
 * flagged conversation can show its underlying status, so flagged conversations are fetched for
 * any selection; and where the badge can show, a New or Unresolved conversation with an open
 * flag shows Leslie Needs Help, so those are fetched when that button is selected. Everything
 * fetched is then narrowed by `matchesSelectedStatuses`.
 */
export const requestedConversationStatuses = (
  selectedStatuses: string[],
  isNeedsHelpBadgeShown: boolean,
): string[] => {
  if (selectedStatuses.length === 0) return selectedStatuses;

  const requestedStatuses = new Set([...selectedStatuses, NEEDS_HELP_STATUS]);
  if (isNeedsHelpBadgeShown && selectedStatuses.includes(NEEDS_HELP_STATUS)) {
    requestedStatuses.add('NEW');
    requestedStatuses.add('UNRESOLVED');
  }
  return Array.from(requestedStatuses);
};

/**
 * Whether a loaded conversation belongs under the selected buttons, judged by the status its card
 * shows right now. Resolving a conversation only updates the cached row, so matching on the
 * status at fetch time would keep it in a queue that excludes Resolved until the next reload.
 */
export const matchesSelectedStatuses = (
  conversation: {
    conversationStatus?: string,
    activeFlowStatus?: string,
    needsHelpMessageIds?: string[],
  },
  selectedStatuses: string[],
  isNeedsHelpBadgeShown: boolean,
): boolean =>
  selectedStatuses.length === 0 ||
  selectedStatuses.includes(
    conversationIndicatorStatus(conversation, isNeedsHelpBadgeShown),
  );
